import type { MedicationPadItemType } from '@/components/Patient/components/PrescriptionPad/PrescriptionPad.types';
import type { PrescriptionPadRules } from '@/state-management/slices/prescriptionAssistant/types';
import { joinDosageSuffix } from './dosageSuffix';
import type { DispenseLimitRow } from './getFormulationDispenseLimitRows';
import { getPadItemMaxDailyDosePhrase } from './getMaxDailyDosePhrase';
import { getPadItemMonthlyDispenseLimitPhrase } from './getMonthlyDispenseLimitPhrase';

/** Which limit a dosage suffix phrase states, so the pad can show each as its own chip */
export type DosageSuffixPhraseKind = 'maxDailyDose' | 'monthlyDispenseLimit';

type DosageSuffixPhrase = {
  kind: DosageSuffixPhraseKind;
  phrase: string;
};

/**
 * The phrases the review step writes to the end of a pad item's dosage, in chip order (the one place it lives):
 * maximum daily dose, then the formulation's monthly dispense limit; none with dispense limits off.
 * @param dispenseLimitRows - every formulation's row, computed once for the pad by the caller
 */
export const getPadItemDosageSuffixPhrases = (
  item: MedicationPadItemType,
  dispenseLimitRows: DispenseLimitRow[],
  rules: PrescriptionPadRules,
): DosageSuffixPhrase[] => {
  if (!rules.enableDispenseLimits) {
    return [];
  }
  const phrases: { kind: DosageSuffixPhraseKind; phrase: string | undefined }[] = [
    { kind: 'maxDailyDose', phrase: getPadItemMaxDailyDosePhrase(item) },
    { kind: 'monthlyDispenseLimit', phrase: getPadItemMonthlyDispenseLimitPhrase(item, dispenseLimitRows) },
  ];
  return phrases.filter((entry): entry is DosageSuffixPhrase => entry.phrase !== undefined);
};

/** `getPadItemDosageSuffixPhrases` joined into the one suffix written to the dosage text; undefined without a phrase */
export const getPadItemDosageSuffix = (
  item: MedicationPadItemType,
  dispenseLimitRows: DispenseLimitRow[],
  rules: PrescriptionPadRules,
) => joinDosageSuffix(getPadItemDosageSuffixPhrases(item, dispenseLimitRows, rules));
